/**
 * Build Facebook plugin iframe src from a post/reel/video permalink.
 * Raw facebook.com URLs do not work as iframe src — use plugins/video.php or post.php.
 *
 * Example (reel):
 * https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Freel%2F…%2F&show_text=false
 */

/** Default plugin size when the iframe fills a ~280px-wide 9:16 wrapper. */
export const FACEBOOK_VIDEO_EMBED_WIDTH = 280;
export const FACEBOOK_VIDEO_EMBED_HEIGHT = Math.round(
  (FACEBOOK_VIDEO_EMBED_WIDTH * 16) / 9,
);

const VIDEO_QUERY_KEYS = ["v", "story_fbid", "id"] as const;

export function isFacebookVideoPermalink(
  permalink: string | null | undefined,
): boolean {
  if (!permalink) return false;
  try {
    const u = new URL(permalink.trim());
    if (u.searchParams.has("v")) return true;
    const path = u.pathname.toLowerCase();
    return (
      /\/(?:reel|reels|videos)\//i.test(path) ||
      /\/watch\/?/i.test(path) ||
      u.hostname.replace(/^www\./, "").toLowerCase() === "fb.watch"
    );
  } catch {
    return (
      /facebook\.com\/(?:[^/]+\/)?videos\//i.test(permalink) ||
      /facebook\.com\/reel\//i.test(permalink) ||
      /facebook\.com\/watch/i.test(permalink) ||
      /fb\.watch\//i.test(permalink)
    );
  }
}

/** Canonical href for Facebook plugins — keep ids on watch/story query strings. */
export function normalizeFacebookEmbedHref(permalink: string): string {
  const raw = permalink.trim();
  try {
    const u = new URL(raw);
    const keep = new URLSearchParams();
    for (const key of VIDEO_QUERY_KEYS) {
      const val = u.searchParams.get(key);
      if (val) keep.set(key, val);
    }
    u.search = keep.toString() ? `?${keep.toString()}` : "";
    u.hash = "";
    if (!u.pathname.endsWith("/") && !keep.toString()) {
      u.pathname = `${u.pathname.replace(/\/+$/, "")}/`;
    }
    return u.toString();
  } catch {
    const base = raw.split("#")[0] ?? raw;
    return base.endsWith("/") ? base : `${base}/`;
  }
}

export function facebookEmbedUrl(
  permalink: string | null | undefined,
  options?: {
    width?: number;
    height?: number;
    showText?: boolean;
    /** Force video.php (e.g. private_content.mediaType === "video"). */
    preferVideo?: boolean;
  },
): string | null {
  const raw = permalink?.trim();
  if (!raw) return null;

  const href = normalizeFacebookEmbedHref(raw);
  const isVideo = options?.preferVideo === true || isFacebookVideoPermalink(raw);
  const width = options?.width ?? FACEBOOK_VIDEO_EMBED_WIDTH;
  const height = options?.height ?? FACEBOOK_VIDEO_EMBED_HEIGHT;
  const showText = options?.showText ?? false;
  const encoded = encodeURIComponent(href);

  if (isVideo) {
    return `https://www.facebook.com/plugins/video.php?height=${height}&href=${encoded}&show_text=${showText}&width=${width}&t=0`;
  }

  return `https://www.facebook.com/plugins/post.php?href=${encoded}&show_text=true&width=${width}`;
}
